import { DataFlow, DataFlowConfiguration, DataFlowRequest, DataObjectDTO } from '@coac-gmbh/saifty-main-apis'
import { SDS_TREE } from './sds'
import { SDS_PASSPORT_ID, DYNAMIC_PASSPORT_ID } from './passports'
import { DropzoneFile } from 'dropzone'
import { IS_MICROBRAINS, IS_INDUSTRIAL_IDENTITIES } from './customers'

export interface FlowData {
  extract: {
    quickExtraction: boolean
    allEchaIngredients: boolean
    allEPAIngredients: boolean
    passportId: string
    displayAllPassportItems: boolean
    sections?: string[]
    languages: string[]
    ocr: boolean
    backlog: boolean
    componentExtraction: boolean
  }
  validate: {
    confidence: number
    lookup_phrases: boolean
    echa_reach_call: boolean
    epa_call: boolean
    region: string
    businessUnit: string
    translate_from_phrases: string[]
    translate_to_phrases: string
    translate_from_document: string[]
    translate_to_document: string
  }
  store: {
    dataassetId: string
    dataassetName: string
  }
}

export interface DataFlowConfigurationParsed extends DataFlowConfiguration {
  data: FlowData
}

// Custom DataFlow structure for the form
export interface FlowWithData extends DataFlowRequest {
  data: FlowData
}

export interface FlowWithControls extends DataFlow {
  selected?: boolean
  isSharedToMe: boolean
  allowUploadFiles?: boolean
  isSendingFiles?: boolean
  filesToUpload: DropzoneFile[]
  filesWithSAPReference: { file: DropzoneFile; SAPDataObject: DataObjectDTO; SAPDocumentRecn?: string }[]
  currentFileToAddReference?: DropzoneFile
  latestConfiguration: { data: FlowData; version: string }
}

export interface documentData {
  dataObjectId: string
  dataAssetId: string
}

export interface FileAlreadyUploaded {
  id: string
  file: DropzoneFile
  name: string
  isUploading: boolean
  isUploaded: boolean
  dataFlowId: string
  dataObjectData?: documentData
  approvalData?: documentData
  SAPReference?: DataObjectDTO
  isSAPDocument?: boolean
  SAPDocumentRecn?: string
}

export const DEFAULT_FLOW_TYPE = 'SDS Extraction'

export const GENERIC_EXTRACTION_FLOW_TYPE = 'Generic Extraction'

export const SPECIFICATION_EXTRACTION_FLOW_TYPE = 'Specification Update'

export const TECHNICAL_DATA_SHEET_FLOW_TYPE = 'TDS Extraction'

export const DEFAULT_FLOW_CONFIDENCE = 95

export const DEFAULT_FLOW_LANGUAGE = 'en'

export type SUPPORTED_LANGUAGES_TYPE = 'EN' | 'DE' | 'FR' | 'ES' | 'NL' | 'ZH'

export type SUPPORTED_REGION_TYPE = 'EMEA' | 'AP' | 'NA' | 'LA'

export const SUPPORTED_REGION_OPTIONS = [
  { name: 'Europe, Middle East, and Africa (EMEA)', value: 'EMEA' },
  { name: 'Asia/Pacific (A/P)', value: 'AP' },
  { name: 'North America (NA)', value: 'NA' },
  { name: 'Latin America (LA)', value: 'LA' },
]

export const SUPPORTED_LANGUAGES_OPTIONS = window.ENV.EXTRACTION_LANGUAGES_OPTIONS
  ? window.ENV.EXTRACTION_LANGUAGES_OPTIONS.split(',').map((code) => code.trim())
  : ['en', 'de', 'fr', 'es', 'zh', 'nl', 'jp', 'pt', 'pl']

export const SUPPORTED_LANGUAGES_OPTIONS_FOR_DOCUMENT = [
  ...new Set([
    ...SUPPORTED_LANGUAGES_OPTIONS,
    'it',
    'hu',
    'el',
    'cs',
    'da',
    'fi',
    'no',
    'ru',
    'sv',
    'sl',
    'sk',
    'tr',
    'id',
    'ms',
    'ko',
    'th',
    'vi',
    'tl',
  ]),
]

export const FLOW_FALLBACK_DATA: FlowData = {
  extract: {
    allEchaIngredients: false,
    allEPAIngredients: false,
    passportId: SDS_PASSPORT_ID,
    displayAllPassportItems: false,
    languages: SUPPORTED_LANGUAGES_OPTIONS,
    quickExtraction: false,
    ocr: false,
    backlog: false,
    componentExtraction: false,
  },
  validate: {
    confidence: DEFAULT_FLOW_CONFIDENCE,
    lookup_phrases: true,
    echa_reach_call: false,
    epa_call: false,
    region: '',
    businessUnit: '',
    // Translation based on phrases catalog
    translate_from_phrases: [],
    translate_to_phrases: '',
    // Translation of document
    translate_from_document: [],
    translate_to_document: '',
  },
  store: {
    dataassetId: '',
    dataassetName: '',
  },
}

export const MICROBRAINS_AND_INDUSTRIAL_DEFAULT_FLOW_DATA: FlowData = {
  extract: {
    allEchaIngredients: false,
    allEPAIngredients: false,
    passportId: DYNAMIC_PASSPORT_ID,
    displayAllPassportItems: false,
    languages: SUPPORTED_LANGUAGES_OPTIONS,
    quickExtraction: true,
    ocr: true,
    backlog: false,
    componentExtraction: false,
  },
  validate: {
    confidence: DEFAULT_FLOW_CONFIDENCE,
    lookup_phrases: true,
    echa_reach_call: false,
    epa_call: false,
    region: '',
    businessUnit: '',
    // Translation based on phrases catalog
    translate_from_phrases: [],
    translate_to_phrases: '',
    // Translation of document
    translate_from_document: [],
    translate_to_document: '',
  },
  store: {
    dataassetId: '',
    dataassetName: '',
  },
}

export const FLOW_FALLBACK_DATA_SECTIONS = SDS_TREE.map((section) => section.id)

export const FLOW_FALLBACK: FlowWithData = {
  name: 'Extraction Fallback',
  flowType: IS_MICROBRAINS || IS_INDUSTRIAL_IDENTITIES ? GENERIC_EXTRACTION_FLOW_TYPE : DEFAULT_FLOW_TYPE,
  data: IS_MICROBRAINS || IS_INDUSTRIAL_IDENTITIES ? MICROBRAINS_AND_INDUSTRIAL_DEFAULT_FLOW_DATA : FLOW_FALLBACK_DATA,
}

export const US_EXTRACTION_REGION_KEY = 'US'

export const SPECIFICATION_UPDATE_KEY = 'SPECIFICATION UPDATE'
